import { useTranslation } from "@sagot/i18n";
import { ErrorState, Skeleton, StatCard } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { useAnnouncementStats } from "./use-announcements";

interface StatsProps {
  audienceName: (code: number) => string;
}

/** Four KPI cards (reachable installs, sent this month, avg open rate, follow-ups answered). */
export function AnnouncementStats({ audienceName }: StatsProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const stats = useAnnouncementStats();
  if (stats.isError) {
    return (
      <div className="card">
        <ErrorState headingLevel={2} title={t("engage.announcements.statsError")} onRetry={() => void stats.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  if (stats.isPending) {
    return (
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden="true">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="card card-body space-y-2">
            <Skeleton className="h-3 w-[120px]" />
            <Skeleton className="h-8 w-[72px]" />
            <Skeleton className="h-3 w-[140px]" />
          </div>
        ))}
      </section>
    );
  }
  const s = stats.data;
  const byAudience = Object.entries(s.reachable_by_audience)
    .map(([code, count]) => `${audienceName(Number(code)) || code} ${fmt.number(count)}`)
    .join(" · ");
  return (
    <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label={t("engage.announcements.title")}>
      <StatCard label={t("engage.announcements.stats.reachable")} value={fmt.number(s.reachable_devices)} footnote={byAudience || t("engage.announcements.stats.reachableNone")} />
      <StatCard label={t("engage.announcements.stats.sentThisMonth")} value={fmt.number(s.sent_this_month)} footnote={t("engage.announcements.stats.pipeline", { scheduled: s.scheduled, drafts: s.drafts })} />
      <StatCard label={t("engage.announcements.stats.avgOpenRate")} value={fmt.percent(s.avg_open_rate)} footnote={t("engage.announcements.stats.openRateHint")} />
      <StatCard label={t("engage.announcements.stats.followUps")} value={fmt.number(s.follow_up_questions_answered)} footnote={t("engage.announcements.stats.followUpsHint")} />
    </section>
  );
}
